'use client';

import React, {
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useCallback
} from 'react';
import Link from 'next/link';
import { useInView } from 'react-intersection-observer';
import { SearchEventResult } from 'searchEvent';
import { getTrendingEvents } from 'services/searchEvent/getTrendingEvents';
import { EventCard } from '@sympla/sympla-components';
import { CarouselNavigator } from 'components/CarouselNavigator/CarouselNavigator';
import { CarouselWrapperSkeleton } from 'components/Matrix/components/CarouselWrapper/CarouselWrapper.skeleton';
import { cardVariants } from 'constants/search';
import { LIMITS, ROUTE_TYPE } from 'constants/index';
import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import locationStore from 'stores/LocationStore';
import {
  analytics,
  analyticsEvents,
  analyticsLabels,
  getParamsRailClicked,
  transportMethods
} from 'lib/analytics';

import * as styles from './TrendingEventsCarousel.css';

export default function TrendingEventsCarousel({
  pageTitleForTracking
}: {
  pageTitleForTracking?: string;
}) {
  const [trendingEventsData, setTrendingEventsData] =
    useState<SearchEventResult | null>(null);

  useEffect(() => {
    let isMounted = true;

    getTrendingEvents(LIMITS.TRACK_MORE_EVENTS).then((response) => {
      if (isMounted) {
        setTrendingEventsData(response);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleTrackAnalyticsClick = useCallback(
    ({ eventName, eventId }: { eventName: string; eventId: number }) => {
      const payload = getParamsRailClicked({
        eventName: eventName,
        eventId: eventId,
        pageTitleForTracking:
          pageTitleForTracking ?? analyticsLabels.pageWithNoResults,
        nameRail: analyticsLabels.trendingEvents
      });
      analytics.sendEvent(analyticsEvents.rail_event_clicked, payload);
    },
    [pageTitleForTracking]
  );

  const carouselOptions: Array<{ slide: ReactNode }> = useMemo(() => {
    if (!trendingEventsData?.data) return [];

    return trendingEventsData.data.map((event, index) => {
      return {
        slide: (
          <EventCard
            id={event.id}
            name={event.name}
            images={event.images}
            sectionPosition={index + 1}
            location={{
              name: event.location?.name,
              city: event.location?.city,
              state: event.location?.state
            }}
            organizer={event.organizer}
            type={event.type}
            url={event.url}
            data
            start_date={event.start_date}
            start_date_formats={event.start_date_formats}
            end_date={event.end_date}
            end_date_formats={event.end_date_formats}
            onClick={() =>
              handleTrackAnalyticsClick({
                eventName: event.name,
                eventId: event.id
              })
            }
          />
        )
      };
    });
  }, [trendingEventsData, pageTitleForTracking, handleTrackAnalyticsClick]);

  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView && trendingEventsData && trendingEventsData.data?.length > 0) {
      analytics.sendEvent(analyticsEvents.rail_event_viewed, {
        item_rail_title: analyticsLabels.trendingEvents,
        item_rail_location: pageTitleForTracking?.trim() || '',
        item_rail_filter: ''
      });
    }
  }, [inView, trendingEventsData, pageTitleForTracking]);

  return (
    <div>
      <div className={styles.titleContainer}>
        <h3 className={styles.sectionTitle}>Eventos em alta</h3>
        <Link
          href={createLinkToRedirect({
            param: `?s=`,
            routeType: ROUTE_TYPE.SEARCH_BAR_DEFAULT_FILTER,
            isAllEvents: true,
            userLocation: locationStore.selectedLocation
          })}
          className={styles.linkViewAll}
          onClick={() =>
            analytics.sendEvent(analyticsEvents.rail_view_more_clicked, {
              item_rail_title: analyticsLabels.trendingEvents,
              transport: transportMethods.sendBeacon
            })
          }
        >
          Ver tudo
        </Link>
      </div>
      <div className={styles.ContentCarousel} ref={ref}>
        {trendingEventsData === null ? (
          <CarouselWrapperSkeleton
            untitled={true}
            type={cardVariants.eventCard}
            numberOfComponents={LIMITS.COLLECTION_EVENTS}
          />
        ) : null}
        <CarouselNavigator
          carouselOptions={carouselOptions}
          typeCarousel={cardVariants.eventCard}
          idCarousel="trending-events-carousel"
        />
      </div>
    </div>
  );
}
